// Performance optimization utilities for Nova Cafe
declare const gtag: Function;
import React, { lazy, ComponentType, LazyExoticComponent } from 'react';

// Lazy loading utility with error boundary
export function createLazyComponent<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  fallback?: React.ComponentType
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      return await importFn();
    } catch (error) {
      console.error('Failed to load component:', error);
      // Return a fallback component on error
      return {
        default: (fallback || (() => React.createElement('div', {
          className: 'p-4 text-center text-red-600'
        }, 'Failed to load component. Please refresh the page.'))) as T
      };
    }
  });
}

// Image optimization utilities
export interface OptimizedImageProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  className?: string;
  lazy?: boolean;
  quality?: number;
  webp?: boolean;
  blur?: boolean;
  onLoad?: () => void;
  onError?: () => void;
}

// OptimizedImage component moved to separate .tsx file due to JSX usage
// This file contains only utility functions

// Generate optimized image URL
function generateOptimizedImageUrl(
  src: string,
  options: {
    width?: number;
    height?: number;
    quality?: number;
    webp?: boolean;
    devicePixelRatio?: number;
  }
): string {
  const { width, height, quality = 80, webp = false, devicePixelRatio = 1 } = options;
  
  // If it's a data URL or external URL, return as-is
  if (src.startsWith('data:') || src.startsWith('http')) {
    return src;
  }
  
  // Create optimized URL for our image service
  const params = new URLSearchParams();
  
  if (width) params.set('w', Math.round(width * devicePixelRatio).toString());
  if (height) params.set('h', Math.round(height * devicePixelRatio).toString());
  params.set('q', quality.toString());
  if (webp) params.set('f', 'webp');
  
  return `/api/images/optimize?src=${encodeURIComponent(src)}&${params.toString()}`;
}

// Check WebP support
function supportsWebP(): boolean {
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  return canvas.toDataURL('image/webp').indexOf('webp') > -1;
}

// Bundle splitting utilities - commented out non-existent components
// export const LazyComponents = {
//   // Add lazy component imports here when components exist
// };

// Performance monitoring
export class PerformanceMonitor {
  private static metrics: Map<string, number> = new Map();
  private static observers = new Set<PerformanceObserver>();

  static init() {
    // Monitor Core Web Vitals
    this.observeLCP();
    this.observeFID();
    this.observeCLS();
    this.observeTTFB();
    
    // Monitor custom metrics
    this.observeNavigationTiming();
    this.observeResourceTiming();
  }

  static measure(name: string, fn: () => void | Promise<void>) {
    const start = performance.now();
    
    const result = fn();
    
    if (result instanceof Promise) {
      return result.finally(() => {
        const duration = performance.now() - start;
        this.metrics.set(name, duration);
        console.log(`Performance: ${name} took ${duration.toFixed(2)}ms`);
      });
    } else {
      const duration = performance.now() - start;
      this.metrics.set(name, duration);
      console.log(`Performance: ${name} took ${duration.toFixed(2)}ms`);
      return result;
    }
  }

  private static observeLCP() {
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];
      console.log('LCP:', lastEntry.startTime);
      this.reportMetric('lcp', lastEntry.startTime);
    });
    
    observer.observe({ entryTypes: ['largest-contentful-paint'] });
    this.observers.add(observer);
  }

  private static observeFID() {
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry) => {
        console.log('FID:', (entry as any).processingStart - entry.startTime);
        this.reportMetric('fid', (entry as any).processingStart - entry.startTime);
      });
    });
    
    observer.observe({ entryTypes: ['first-input'] });
    this.observers.add(observer);
  }

  private static observeCLS() {
    let clsValue = 0;
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry: any) => {
        if (!entry.hadRecentInput) {
          clsValue += entry.value;
        }
      });
      console.log('CLS:', clsValue);
      this.reportMetric('cls', clsValue);
    });
    
    observer.observe({ entryTypes: ['layout-shift'] });
    this.observers.add(observer);
  }

  private static observeTTFB() {
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry: any) => {
        const ttfb = entry.responseStart - entry.requestStart;
        console.log('TTFB:', ttfb);
        this.reportMetric('ttfb', ttfb);
      });
    });
    
    observer.observe({ entryTypes: ['navigation'] });
    this.observers.add(observer);
  }

  private static observeNavigationTiming() {
    window.addEventListener('load', () => {
      const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
      
      console.log('Navigation Timing:', {
        domContentLoaded: navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart,
        load: navigation.loadEventEnd - navigation.loadEventStart,
        total: navigation.loadEventEnd - navigation.fetchStart,
      });
    });
  }

  private static observeResourceTiming() {
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry) => {
        if (entry.duration > 100) { // Log slow resources
          console.log('Slow resource:', entry.name, entry.duration);
        }
      });
    });
    
    observer.observe({ entryTypes: ['resource'] });
    this.observers.add(observer);
  }

  private static reportMetric(name: string, value: number) {
    // Report to analytics service
    if (typeof gtag !== 'undefined') {
      gtag('event', 'web_vitals', {
        event_category: 'performance',
        event_label: name,
        value: Math.round(value),
        non_interaction: true,
      });
    }
  }

  static getMetrics() {
    return Object.fromEntries(this.metrics);
  }

  static cleanup() {
    this.observers.forEach(observer => observer.disconnect());
    this.observers.clear();
  }
}

// Network optimization
export class NetworkOptimizer {
  private static prefetchedResources = new Set<string>();
  
  static prefetchResource(url: string, type: 'script' | 'style' | 'image' | 'fetch' = 'fetch') {
    if (this.prefetchedResources.has(url)) return;
    
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.as = type;
    link.href = url;
    
    document.head.appendChild(link);
    this.prefetchedResources.add(url);
  }

  static preloadCriticalResource(url: string, type: 'script' | 'style' | 'image' | 'font') {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = type;
    link.href = url;
    
    if (type === 'font') {
      link.crossOrigin = 'anonymous';
    }
    
    document.head.appendChild(link);
  }

  static enableResourceHints() {
    // DNS prefetch for external domains
    const domains = ['fonts.googleapis.com', 'fonts.gstatic.com'];
    
    domains.forEach(domain => {
      const link = document.createElement('link');
      link.rel = 'dns-prefetch';
      link.href = `//${domain}`;
      document.head.appendChild(link);
    });
  }
}

// Memory optimization
export class MemoryOptimizer {
  private static cleanupTasks: (() => void)[] = [];
  
  static addCleanupTask(task: () => void) {
    this.cleanupTasks.push(task);
  }
  
  static cleanup() {
    this.cleanupTasks.forEach(task => {
      try {
        task();
      } catch (error) {
        console.error('Cleanup task failed:', error);
      }
    });
    this.cleanupTasks.length = 0;
  }
  
  static monitorMemoryUsage() {
    if ('memory' in performance) {
      const memory = (performance as any).memory;
      console.log('Memory usage:', {
        used: Math.round(memory.usedJSHeapSize / 1048576) + ' MB',
        total: Math.round(memory.totalJSHeapSize / 1048576) + ' MB',
        limit: Math.round(memory.jsHeapSizeLimit / 1048576) + ' MB',
      });
      
      // Warn if memory usage is high
      if (memory.usedJSHeapSize / memory.jsHeapSizeLimit > 0.8) {
        console.warn('High memory usage detected. Consider cleaning up unused resources.');
      }
    }
  }
}

// Initialize performance monitoring
if (typeof window !== 'undefined') {
  PerformanceMonitor.init();
  NetworkOptimizer.enableResourceHints();
  
  // Monitor memory usage every 30 seconds in development
  if (process.env.NODE_ENV === 'development') {
    setInterval(() => MemoryOptimizer.monitorMemoryUsage(), 30000);
  }
}

